import { ApiError } from "@/api/client";
import { fetchMunicipios, fetchStaticLookups, searchCnaes, searchNaturezas } from "@/api/lookups";
import { downloadCsv, searchCompanies } from "@/api/search";
import { CnpjDrawer } from "@/components/CnpjDrawer";
import { FilterForm } from "@/components/FilterForm";
import type { MultiSelectOption } from "@/components/MultiSelect";
import { Pagination } from "@/components/Pagination";
import { ResultsTable } from "@/components/ResultsTable";
import { SaveShareModal } from "@/components/SaveShareModal";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { EmptyState, ErrorState } from "@/components/ui/states";
import { Toggle } from "@/components/ui/toggle";
import {
  buildSearchParams,
  countActiveFilters,
  parseSearchState,
  serializeFiltersOnly,
} from "@/lib/filters";
import {
  type ComboboxItem,
  emptyFilterFormValues,
  filtersToFormValues,
  formValuesToFilters,
} from "@/lib/form";
import { formatCnae, formatNumber, onlyDigits } from "@/lib/format";
import { UI } from "@/lib/labels";
import { markEnd, markStart } from "@/lib/performance";
import type { Municipio, SearchFilters, SearchResponse, StaticLookups } from "@/types/api";
import { Download, Search, SearchX } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

const cnaePlaceholder = (codigo: number): string => formatCnae(codigo);
const naturezaPlaceholder = (codigo: number): string => String(codigo);
const cnaeItemLabel = (codigo: number, descricao: string): string =>
  `${formatCnae(codigo)} — ${descricao}`;
const naturezaItemLabel = (codigo: number, descricao: string): string => `${codigo} — ${descricao}`;

const SKELETON_ROWS = Array.from({ length: 8 }, (_, index) => `skeleton-${index}`);

function codesToItems(
  codes: number[] | undefined,
  placeholder: (codigo: number) => string,
): ComboboxItem[] {
  return (codes ?? []).map((codigo) => ({ codigo, label: placeholder(codigo) }));
}

/** Reaproveita rótulos já resolvidos ao re-hidratar a partir da URL. */
function reconcileItems(
  codes: number[],
  previous: ComboboxItem[],
  placeholder: (codigo: number) => string,
): ComboboxItem[] {
  const labelByCode = new Map(previous.map((item) => [item.codigo, item.label]));
  return codes.map((codigo) => ({ codigo, label: labelByCode.get(codigo) ?? placeholder(codigo) }));
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError || error instanceof Error) {
    return error.message;
  }
  return "Erro inesperado. Tente novamente.";
}

interface SearchViewState {
  status: "idle" | "loading" | "success" | "error";
  data: SearchResponse | null;
  error: string | null;
}

/** Página de pesquisa avançada — filtros, resultados, paginação e export. */
export function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const paramsString = searchParams.toString();
  const hasSearch = paramsString.length > 0;

  const committed = useMemo(
    () => parseSearchState(new URLSearchParams(paramsString)),
    [paramsString],
  );
  const filterKey = serializeFiltersOnly(committed.filters);

  const [values, setValues] = useState(() =>
    filtersToFormValues(
      committed.filters,
      codesToItems(committed.filters.cnae, cnaePlaceholder),
      codesToItems(committed.filters.natureza, naturezaPlaceholder),
    ),
  );

  const [staticLookups, setStaticLookups] = useState<StaticLookups | null>(null);
  const [municipios, setMunicipios] = useState<MultiSelectOption[]>([]);
  const [municipiosLoading, setMunicipiosLoading] = useState(false);
  const [view, setView] = useState<SearchViewState>(() => ({
    status: hasSearch ? "loading" : "idle",
    data: null,
    error: null,
  }));
  const [saveOpen, setSaveOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState(false);
  const [drawerCnpj, setDrawerCnpj] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  // Assinatura de filtros dos resultados atualmente exibidos: permite manter os
  // dados durante a paginação (mesmos filtros) e zerá-los numa nova busca — para
  // que resultados de filtros antigos nunca apareçam junto de novos filtros.
  const dataFilterKeyRef = useRef<string | null>(null);

  const draftFilters = useMemo(() => formValuesToFilters(values), [values]);

  // Sincroniza o formulário quando a parte de filtros da URL muda (deep-link,
  // voltar/avançar, aplicar salva). Preserva rótulos de CNAE/natureza por código.
  useEffect(() => {
    const parsed = parseSearchState(new URLSearchParams(filterKey)).filters;
    setValues((previous) => {
      const cnaeItems = reconcileItems(parsed.cnae ?? [], previous.cnaeItems, cnaePlaceholder);
      const naturezaItems = reconcileItems(
        parsed.natureza ?? [],
        previous.naturezaItems,
        naturezaPlaceholder,
      );
      return filtersToFormValues(parsed, cnaeItems, naturezaItems);
    });
  }, [filterKey]);

  // Carrega os lookups estáticos uma vez.
  useEffect(() => {
    let active = true;
    fetchStaticLookups()
      .then((lookups) => {
        if (active) {
          setStaticLookups(lookups);
        }
      })
      .catch(() => {
        // Degrada para os fallbacks locais (labels.ts).
      });
    return () => {
      active = false;
    };
  }, []);

  // Municípios das UFs selecionadas (dependente da UF).
  const ufKey = useMemo(() => [...values.uf].sort().join(","), [values.uf]);
  useEffect(() => {
    const ufs = ufKey ? ufKey.split(",") : [];
    if (ufs.length === 0) {
      setMunicipios([]);
      return;
    }
    let active = true;
    setMunicipiosLoading(true);
    Promise.all(
      ufs.map(async (uf) => ({
        uf,
        list: await fetchMunicipios(uf).catch(() => [] as Municipio[]),
      })),
    )
      .then((results) => {
        if (!active) {
          return;
        }
        const options: MultiSelectOption[] = [];
        const seen = new Set<number>();
        for (const { uf, list } of results) {
          for (const item of list) {
            if (seen.has(item.codigo)) {
              continue;
            }
            seen.add(item.codigo);
            options.push({
              value: String(item.codigo),
              label: ufs.length > 1 ? `${item.nome} — ${uf}` : item.nome,
            });
          }
        }
        options.sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));
        setMunicipios(options);
        setMunicipiosLoading(false);
        // Remove municípios que não pertencem mais às UFs selecionadas.
        setValues((previous) => {
          const valid = new Set(options.map((option) => option.value));
          const filtered = previous.municipio.filter((value) => valid.has(value));
          return filtered.length === previous.municipio.length
            ? previous
            : { ...previous, municipio: filtered };
        });
      })
      .catch(() => {
        if (active) {
          setMunicipiosLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [ufKey]);

  // Executa a pesquisa quando a URL (filtros/página) muda ou ao "Tentar
  // novamente" (reloadToken). Resiliente ao double-invoke do StrictMode: cada
  // execução cancela a anterior (AbortController) e ignora respostas abortadas.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reloadToken é um nonce que força repetir a busca (retry) sem alterar a URL.
  useEffect(() => {
    if (!hasSearch) {
      setView({ status: "idle", data: null, error: null });
      dataFilterKeyRef.current = null;
      return;
    }
    const controller = new AbortController();
    // Mantém os resultados durante o loading APENAS quando só a página muda
    // (mesmos filtros). Numa nova busca, zera para não misturar filtros.
    const sameFilters = dataFilterKeyRef.current === filterKey;
    markStart("search");
    setView((previous) => ({
      status: "loading",
      data: sameFilters ? previous.data : null,
      error: null,
    }));
    searchCompanies(
      { filters: committed.filters, page: committed.page, pageSize: committed.pageSize },
      controller.signal,
    )
      .then((response) => {
        if (controller.signal.aborted) {
          return;
        }
        markEnd("search");
        dataFilterKeyRef.current = filterKey;
        setView({ status: "success", data: response, error: null });
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) {
          return;
        }
        // Falha de busca (500/503/timeout): descarta resultados stale — nunca
        // podem parecer válidos para os novos filtros. Exibe erro + "Tentar
        // novamente"; os filtros do usuário ficam intactos (formulário/URL).
        dataFilterKeyRef.current = null;
        setView({ status: "error", data: null, error: errorMessage(error) });
      });
    return () => controller.abort();
  }, [committed, hasSearch, filterKey, reloadToken]);

  // Resolve os rótulos (descrições) de CNAEs vindos por deep-link (só códigos).
  const unresolvedCnaeKey = useMemo(
    () =>
      values.cnaeItems
        .filter((item) => item.label === cnaePlaceholder(item.codigo))
        .map((item) => item.codigo)
        .join(","),
    [values.cnaeItems],
  );
  useEffect(() => {
    if (!unresolvedCnaeKey) {
      return;
    }
    const codes = unresolvedCnaeKey.split(",").map(Number);
    let active = true;
    Promise.all(
      codes.map(async (codigo) => {
        try {
          const list = await searchCnaes(String(codigo));
          const match = list.find((item) => item.codigo === codigo);
          return match ? { codigo, label: cnaeItemLabel(codigo, match.descricao) } : null;
        } catch {
          return null;
        }
      }),
    ).then((resolved) => {
      if (!active) {
        return;
      }
      const pairs = resolved.filter((item): item is ComboboxItem => item !== null);
      if (pairs.length === 0) {
        return;
      }
      const labelByCode = new Map(pairs.map((pair) => [pair.codigo, pair.label]));
      setValues((previous) => ({
        ...previous,
        cnaeItems: previous.cnaeItems.map((item) => {
          const label = labelByCode.get(item.codigo);
          return label ? { codigo: item.codigo, label } : item;
        }),
      }));
    });
    return () => {
      active = false;
    };
  }, [unresolvedCnaeKey]);

  // Resolve os rótulos das naturezas vindas por deep-link.
  const unresolvedNaturezaKey = useMemo(
    () =>
      values.naturezaItems
        .filter((item) => item.label === naturezaPlaceholder(item.codigo))
        .map((item) => item.codigo)
        .join(","),
    [values.naturezaItems],
  );
  useEffect(() => {
    if (!unresolvedNaturezaKey) {
      return;
    }
    const codes = unresolvedNaturezaKey.split(",").map(Number);
    let active = true;
    Promise.all(
      codes.map(async (codigo) => {
        try {
          const list = await searchNaturezas(String(codigo));
          const match = list.find((item) => item.codigo === codigo);
          return match ? { codigo, label: naturezaItemLabel(codigo, match.descricao) } : null;
        } catch {
          return null;
        }
      }),
    ).then((resolved) => {
      if (!active) {
        return;
      }
      const pairs = resolved.filter((item): item is ComboboxItem => item !== null);
      if (pairs.length === 0) {
        return;
      }
      const labelByCode = new Map(pairs.map((pair) => [pair.codigo, pair.label]));
      setValues((previous) => ({
        ...previous,
        naturezaItems: previous.naturezaItems.map((item) => {
          const label = labelByCode.get(item.codigo);
          return label ? { codigo: item.codigo, label } : item;
        }),
      }));
    });
    return () => {
      active = false;
    };
  }, [unresolvedNaturezaKey]);

  const cnaeSearch = useCallback(
    (query: string, signal: AbortSignal): Promise<ComboboxItem[]> =>
      searchCnaes(query, signal).then((list) =>
        list.map((item) => ({
          codigo: item.codigo,
          label: cnaeItemLabel(item.codigo, item.descricao),
        })),
      ),
    [],
  );

  const naturezaSearch = useCallback(
    (query: string, signal: AbortSignal): Promise<ComboboxItem[]> =>
      searchNaturezas(query, signal).then((list) =>
        list.map((item) => ({
          codigo: item.codigo,
          label: naturezaItemLabel(item.codigo, item.descricao),
        })),
      ),
    [],
  );

  const handleSubmit = useCallback(() => {
    // Busca por sócio: chave alternativa (pessoa → empresas). Vai para /socio,
    // cujos resultados têm colunas próprias; ignora todos os filtros de empresa.
    // Casa por CNPJ do sócio PJ (14 díg.) ou por nome exato (mín. 3 caracteres).
    const socioTrim = values.socioInput.trim();
    if (socioTrim.length > 0) {
      const socioParams = new URLSearchParams();
      const socioDigits = onlyDigits(socioTrim);
      if (socioDigits.length === 14) {
        socioParams.set("documento", socioDigits);
      } else if ([...socioTrim].length >= 3) {
        socioParams.set("nome", socioTrim);
      } else {
        return; // inválido — a validação inline no formulário orienta o usuário.
      }
      navigate(`/socio?${socioParams.toString()}`, {
        state: { from: paramsString ? `/?${paramsString}` : "/" },
      });
      return;
    }

    const cnpjDigits = values.cnpjInput;
    // CNPJ completo (14 díg.) → abre a ficha direto, sem pesquisar.
    if (cnpjDigits.length === 14) {
      navigate(`/cnpj/${cnpjDigits}`, {
        state: { from: paramsString ? `/?${paramsString}` : "/" },
      });
      return;
    }
    // CNPJ incompleto (1-7 ou 9-13 díg.) → validação inline no form, sem chamar API.
    if (cnpjDigits.length > 0 && cnpjDigits.length !== 8) {
      return;
    }
    // 8 díg. → só cnpjBasico; vazio → filtros normais (formValuesToFilters decide).
    setSearchParams(buildSearchParams(formValuesToFilters(values), 1, committed.pageSize));
  }, [values, committed.pageSize, setSearchParams, navigate, paramsString]);

  const handleClear = useCallback(() => {
    setValues(emptyFilterFormValues());
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  const handlePageChange = useCallback(
    (page: number) => {
      setSearchParams(buildSearchParams(committed.filters, page, committed.pageSize));
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    [committed.filters, committed.pageSize, setSearchParams],
  );

  const handlePageSizeChange = useCallback(
    (size: number) => {
      setSearchParams(buildSearchParams(committed.filters, 1, size));
    },
    [committed.filters, setSearchParams],
  );

  const handleApplySaved = useCallback(
    (filters: SearchFilters) => {
      setSearchParams(buildSearchParams(filters, 1, committed.pageSize));
    },
    [committed.pageSize, setSearchParams],
  );

  const handleExport = useCallback(async () => {
    setExporting(true);
    setExportError(null);
    try {
      await downloadCsv(committed.filters);
    } catch (error) {
      setExportError(errorMessage(error));
    } finally {
      setExporting(false);
    }
  }, [committed.filters]);

  // Repete a busca atual mesmo com a URL inalterada (ex.: após erro transitório).
  const handleRetry = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  const shareUrl = useMemo(() => {
    const params = buildSearchParams(draftFilters, 1, committed.pageSize);
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    return `${origin}/?${params.toString()}`;
  }, [draftFilters, committed.pageSize]);

  const activeFilterCount = countActiveFilters(draftFilters);
  const detailFrom = `/?${paramsString}`;
  const isLoading = view.status === "loading";

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-foreground">Pesquisa de empresas</h1>
        <p className="text-sm text-muted-foreground">
          Filtre a base de CNPJs da Receita Federal para prospecção B2B.
        </p>
      </div>

      <FilterForm
        values={values}
        setValues={setValues}
        staticLookups={staticLookups}
        municipios={municipios}
        municipiosLoading={municipiosLoading}
        cnaeSearch={cnaeSearch}
        naturezaSearch={naturezaSearch}
        onSubmit={handleSubmit}
        onClear={handleClear}
        onSaveShare={() => setSaveOpen(true)}
        searching={isLoading}
        activeFilterCount={activeFilterCount}
      />

      {!hasSearch ? (
        <EmptyState
          icon={<Search className="h-10 w-10" aria-hidden="true" />}
          title="Comece sua pesquisa"
          description="Use os filtros acima e clique em Pesquisar para encontrar empresas."
        />
      ) : (
        <div className="rounded-lg border border-border bg-card shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              {view.data ? (
                <span>
                  <span className="font-semibold text-foreground">
                    {view.data.totalEstimado ? "≈ " : ""}
                    {formatNumber(view.data.total)}
                  </span>{" "}
                  {view.data.total === 1 ? "empresa encontrada" : "empresas encontradas"}
                  {view.data.totalEstimado ? " (estimativa)" : ""}
                </span>
              ) : isLoading ? (
                <span>Buscando…</span>
              ) : null}
              {isLoading && view.data ? <Spinner className="h-4 w-4" /> : null}
            </div>
            <div className="flex items-center gap-3">
              <Toggle
                label="Abrir em painel lateral"
                checked={drawerMode}
                onChange={(value) => {
                  setDrawerMode(value);
                  if (!value) {
                    setDrawerCnpj(null);
                  }
                }}
                className="w-auto"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={handleExport}
                disabled={exporting || !view.data || view.data.total === 0}
              >
                {exporting ? (
                  <Spinner className="h-4 w-4" />
                ) : (
                  <Download className="h-4 w-4" aria-hidden="true" />
                )}
                {UI.actions.exportCsv}
              </Button>
            </div>
          </div>

          {exportError ? (
            <p className="border-b border-border px-4 py-2 text-xs text-destructive" role="alert">
              {exportError}
            </p>
          ) : null}

          {view.status === "error" ? (
            <ErrorState
              title="Não foi possível pesquisar"
              message={view.error ?? "Falha na pesquisa. Verifique sua conexão e tente novamente."}
              onRetry={handleRetry}
            />
          ) : isLoading && !view.data ? (
            <div className="space-y-2 p-4">
              {SKELETON_ROWS.map((id) => (
                <Skeleton key={id} className="h-11 w-full" />
              ))}
            </div>
          ) : view.data && view.data.items.length === 0 ? (
            <EmptyState
              icon={<SearchX className="h-10 w-10" aria-hidden="true" />}
              title="Nenhum resultado"
              description="Nenhuma empresa corresponde aos filtros. Ajuste os critérios e tente novamente."
            />
          ) : view.data ? (
            <>
              <ResultsTable
                items={view.data.items}
                detailFrom={detailFrom}
                drawerMode={drawerMode}
                onOpenRecord={setDrawerCnpj}
              />
              <Pagination
                page={committed.page}
                pageSize={committed.pageSize}
                total={view.data.total}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                disabled={isLoading}
              />
            </>
          ) : null}
        </div>
      )}

      <SaveShareModal
        open={saveOpen}
        onClose={() => setSaveOpen(false)}
        currentFilters={draftFilters}
        shareUrl={shareUrl}
        onApply={handleApplySaved}
      />

      <CnpjDrawer
        cnpj={drawerCnpj}
        open={drawerCnpj !== null}
        onClose={() => setDrawerCnpj(null)}
        listUrl={detailFrom}
      />
    </div>
  );
}
